import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Sparkles, ShoppingBag, ChevronLeft, ChevronRight, Store, ArrowRight, LayoutGrid } from "lucide-react"; 
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";

import { storefrontApi } from "@/lib/extensions-api";
import { formatNumber } from "@/lib/format";
import { Skeleton } from "@/components/ui/Skeleton";
import type { HeroSliderItem } from "@/types/storefront";

// Custom Formatter for Tk
function formatPriceTk(amount: string | number) {
  const n = typeof amount === "string" ? Number(amount) : amount;
  const numStr = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
  return `Tk ${numStr}`;
}

export function StorefrontPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["public-storefront"],
    queryFn: storefrontApi.publicData,
  });

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-12 sm:space-y-20 animate-fade-in">
        
        {isLoading ? (
          <div className="space-y-12">
            <Skeleton className="h-[250px] sm:h-[350px] w-full rounded-[2rem]" />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-[1.5rem]" />)}
            </div>
          </div>
        ) : data ? (
          <>
            {/* 📸 Modern, Clean Hero Slider (Images Only) */}
            {data.hero_sliders.length > 0 && <HeroSlider sliders={data.hero_sliders} />}

            {/* 📦 Dynamic Sections */}
            <div className="space-y-16">
              {data.sections.map((section: any) => (
                <section key={section.id}>
                  
                  {/* Banner Type Section */}
                  {section.section_type === "banner" && section.banner_image && (
                    <Link to={section.banner_url || "#"} className="block group rounded-[2rem] overflow-hidden shadow-sm hover:shadow-xl transition-all border border-surface-200/60 relative">
                      <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors z-10" />
                      <img src={section.banner_image} alt={section.title} className="w-full h-[200px] sm:h-[300px] object-cover group-hover:scale-105 transition-transform duration-700" />
                    </Link>
                  )}

                  {/* Category ONLY Section (Horizontal Scroll/Swipe) */}
                  {section.section_type === "category_only" && (
                    <div className="relative">
                      {/* Section Header */}
                      <div className="flex items-center justify-between mb-4 sm:mb-5">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-5 sm:h-6 bg-brand-500 rounded-full" />
                          <h2 className="text-xl sm:text-2xl font-extrabold text-surface-900 tracking-tight">{section.title}</h2>
                        </div>
                        <Link 
                          to="/categories" 
                          className="text-xs sm:text-sm font-bold text-brand-600 hover:text-brand-800 transition-colors flex items-center gap-1 bg-brand-50 px-3 py-1.5 rounded-full"
                        >
                          View All <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>

                      <div className="flex overflow-x-auto gap-3 sm:gap-4 pb-4 snap-x snap-mandatory hide-scrollbar">
                        {section.categories_data?.map((cat: any) => (
                          <Link
                            key={cat.id}
                            to={`/catalog?category=${cat.id}`}
                            className="group bg-white border border-surface-200 rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center text-center hover:border-brand-300 hover:shadow-md transition-all duration-300 shrink-0 w-[100px] sm:w-[130px] lg:w-[150px] snap-start"
                          >
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-brand-50 to-surface-100 border border-surface-200 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                              <LayoutGrid className="w-6 h-6 sm:w-7 sm:h-7 text-brand-600" />
                            </div>
                            <h3 className="font-bold text-surface-900 text-xs sm:text-sm line-clamp-2 leading-tight group-hover:text-brand-700 transition-colors">
                              {cat.name}
                            </h3>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Product Group Section */}
                  {["category_group", "product_group"].includes(section.section_type) && (
                    <>
                      <div className="flex items-end justify-between mb-6 sm:mb-8">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-1.5 h-5 sm:h-6 bg-brand-500 rounded-full" />
                            <h2 className="text-xl sm:text-2xl font-extrabold text-surface-900 tracking-tight">{section.title}</h2>
                          </div>
                          {section.category_name && <p className="text-xs sm:text-sm text-surface-500 ml-3.5 font-medium">Category: {section.category_name}</p>}
                        </div>
                        <Link to="/catalog" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                          View all <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                      
                      {section.products_data && section.products_data.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5">
                          {section.products_data.map((product: any) => (
                            <ProductCardMini key={product.id} product={product} />
                          ))}
                        </div>
                      ) : (
                        <div className="py-12 text-center bg-white border border-surface-200 border-dashed rounded-[2rem] text-surface-500 font-medium flex flex-col items-center justify-center">
                          <ShoppingBag className="w-10 h-10 text-surface-300 mb-3" />
                          No products available in this section.
                        </div>
                      )}
                    </>
                  )}
                </section>
              ))}
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}

// --- Sub Components ---

function HeroSlider({ sliders }: { sliders: HeroSliderItem[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % sliders.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [sliders.length]);

  if (!sliders || sliders.length === 0) return null;

  const slide = sliders[index];

  return (
    // 🛠️ FIXED: Cleaned up background, borders, and overlays. Just a pure image slider!
    <div className="relative w-full h-[200px] sm:h-[300px] lg:h-[400px] rounded-2xl lg:rounded-[2rem] bg-surface-50 overflow-hidden shadow-sm group">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {slide.image ? (
            <img src={slide.image} alt="Hero Banner" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-surface-200" />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Controls */}
      {sliders.length > 1 && (
        <>
          <button onClick={(e) => { e.preventDefault(); setIndex((i) => (i - 1 + sliders.length) % sliders.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/20 hover:bg-black/40 text-white rounded-full flex items-center justify-center backdrop-blur transition-all opacity-0 group-hover:opacity-100 z-10">
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button onClick={(e) => { e.preventDefault(); setIndex((i) => (i + 1) % sliders.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/20 hover:bg-black/40 text-white rounded-full flex items-center justify-center backdrop-blur transition-all opacity-0 group-hover:opacity-100 z-10">
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div className="absolute bottom-3 sm:bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
            {sliders.map((_, i) => (
              <button key={i} onClick={(e) => { e.preventDefault(); setIndex(i); }} className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 sm:w-8 bg-white shadow" : "w-1.5 sm:w-2 bg-white/50 hover:bg-white/80 shadow"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ProductCardMini({ product }: { product: any }) {
  const primary = product.images?.find((i: any) => i.is_primary) ?? product.images?.[0] ?? null;

  const price1 = Number(product.base_price) || 0;
  const price2 = product.compare_at_price ? Number(product.compare_at_price) : 0;

  let sellingPrice = price1;
  let crossedOutPrice = null;

  if (price2 > 0) {
    if (price2 > price1) {
      crossedOutPrice = price2;
      sellingPrice = price1;
    } else {
      crossedOutPrice = price1;
      sellingPrice = price2;
    }
  }

  return (
    <Link to={`/catalog/${product.product_code}`} className="group relative block bg-white rounded-[1.25rem] border border-surface-200/80 overflow-hidden shadow-sm hover:shadow-elevate-2 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full z-10 hover:z-20">
      
      <div className="aspect-[4/5] bg-white relative overflow-hidden shrink-0 flex items-center justify-center">
        {primary ? (
          <img src={primary.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-surface-300 bg-surface-100/50">
            <ShoppingBag className="h-10 w-10 opacity-30" />
          </div>
        )}
        
        <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start z-10">
          {crossedOutPrice && (
            <div className="bg-danger-600/95 backdrop-blur text-white text-[10px] px-2 py-0.5 rounded-md font-bold shadow-sm">
              SALE
            </div>
          )}
        </div>
      </div>

      <div className="p-3 sm:p-4 flex flex-col flex-1 bg-white relative z-10">
        <h3 className="font-bold text-surface-900 text-xs sm:text-sm line-clamp-2 leading-snug flex-1 group-hover:text-brand-600 transition-colors">
          {product.name}
        </h3>
        
        {product.vendor_shop_name && (
          <div className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-warning-700 bg-warning-50 px-1.5 py-0.5 rounded w-fit border border-warning-100 shrink-0">
            <Store className="w-3 h-3" /> {product.vendor_shop_name}
          </div>
        )}
        
        <div className="mt-3 sm:mt-4 pt-2 border-t border-surface-100/80 flex items-center justify-between min-h-[40px]">
          
          <div className="flex flex-col justify-center">
            {crossedOutPrice && (
              <div className="text-[10px] text-surface-400 line-through leading-none mb-0.5">
                {formatPriceTk(crossedOutPrice)}
              </div>
            )}
            <div className="text-xs sm:text-sm font-extrabold text-brand-700 leading-none tracking-tight">
              {formatPriceTk(sellingPrice)}
            </div>
          </div>
          
          {product.qualification_point_value > 0 && (
            <div className="flex items-center gap-1 text-brand-600 text-xs sm:text-sm font-extrabold leading-none">
              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              {product.qualification_point_value} PV
            </div>
          )}
          
        </div>
      </div>
    </Link>
  );
}